import React, {useState} from 'react';
import Pagination from 'react-js-pagination';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {toast} from 'react-toastify';
import {
    accountPagingAction,
    approveInternalSoftwareAccessAction
} from '@store/reducers/account';
import Table from 'react-bootstrap/Table';
import {useQuery} from 'react-query';
import {NIL as NIL_UUID} from 'uuid';
import SelectTree from '@app/components/select-tree/SelectTree';
import {createUrlWithParam} from '@app/utils/helper';
import {URI_INTERNAL} from '@app/utils/pathLocations';
import {objectShowNumber} from '@app/utils/define';
import {ACCOUNT_API, ROLE_API} from '@app/utils/apiUrl';
import {Get} from '@app/utils/httpProvider';

const PENDING_PARENT_ROLE = 'PENDINGUSER';
const PENDING_ROLE_PREFIX = 'PENDINGUSER_';

const getRoleName = (item) => typeof item === 'string'
    ? item
    : item?.name ?? item?.Name ?? '';
const getAccountRoles = (account) => {
    const roles = account?.roles ?? account?.Roles ?? [];
    return Array.isArray(roles) ? roles : roles?.$values ?? [];
};
const normalizeSelectedRoles = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.$values)) return value.$values;
    return value ? [value] : [];
};
const isPendingRole = (item) => {
    const roleName = getRoleName(item).toUpperCase();
    return roleName === PENDING_PARENT_ROLE || roleName.startsWith(PENDING_ROLE_PREFIX);
};

const DuyetAccount = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const valueSearch = useSelector((state) => state.search.valueSearch);
    const totalAccount = useSelector((state) => state.account.totalAccount);
    const listAccount = useSelector((state) => state.account.listAccount);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [search, setSearch] = useState('');
    const [isPendingUser, setIsPendingUser] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [isApproving, setIsApproving] = useState(false);

    const accountIds = [...new Set((listAccount ?? [])
        .map((item) => item.id ?? item.Id)
        .filter(Boolean))];
    const accountInfoQuery = useQuery(
        ['accountInfoForAccountApproval', accountIds],
        () => Get(ACCOUNT_API.GET_PAGING_ACCOUNT_FROM_APP_DUNGCHUNG, null, {
            LstAccountId: accountIds,
            // Account IDs already belong to the current account page.
            PageIndex: 1,
            PageSize: accountIds.length
        }),
        {
            enabled: accountIds.length > 0,
            onError: () => toast.error('Không thể tải thông tin đơn vị của tài khoản')
        }
    );
    const accountInfoData = accountInfoQuery.data?.data ?? accountInfoQuery.data?.Data ?? [];
    const accountInfoItems = Array.isArray(accountInfoData)
        ? accountInfoData : accountInfoData?.$values ?? [];
    const accountInfoByAccountId = new Map(accountInfoItems.map((item) => [
        (item.accountId ?? item.AccountId)?.toLowerCase(), item
    ]));

    const listRole = useQuery(['getPendingRolesForAccountApproval'], () =>
        Get(ROLE_API.GET_PAGING, null, {PageIndex: 1, PageSize: 10000})
    );
    const roleData = listRole.data?.data ?? listRole.data?.Data ?? [];
    const roleItems = Array.isArray(roleData) ? roleData : roleData?.$values ?? [];
    const lstRoleFlat = roleItems
        .filter(isPendingRole)
        .map((item) => ({
            id: item.id ?? item.Id,
            name: getRoleName(item),
            parentId: item.parentId ?? item.ParentId ?? NIL_UUID
        }));

    const paramUrl = new URLSearchParams(location.search.substring(1));
    if (
        paramUrl.get('page') != null &&
        parseInt(paramUrl.get('page'), 10) !== page
    ) {
        const pageResult = parseInt(paramUrl.get('page'), 10);
        setPage(pageResult);
    }
    if (
        paramUrl.get('size') != null &&
        parseInt(paramUrl.get('size'), 10) !== pageSize
    ) {
        const sizeResult = parseInt(paramUrl.get('size'), 10);
        setPageSize(sizeResult);
    }
    if (paramUrl.get('search') != null && paramUrl.get('search') !== search) {
        setSearch(paramUrl.get('search'));
    }
    if (paramUrl.get('pending') != null) {
        const pendingValue = paramUrl.get('pending') === 'true';
        if (pendingValue !== isPendingUser) setIsPendingUser(pendingValue);
    }

    const model = {
        query: search
    };

    const replaceHistory = async (Page, Size, Search, IsPendingUser = isPendingUser) => {
        const modelPost = {
            Keyword: Search,
            PageIndex: Page,
            PageSize: Size,
            IsPendingUser
        };
        await dispatch(accountPagingAction(modelPost));

        history.replace(
            createUrlWithParam(URI_INTERNAL.DUYET_ACCOUNT_PATH, {
                page: Page,
                size: Size,
                search: Search,
                pending: IsPendingUser
            })
        );
    };

    React.useEffect(() => {
        if (valueSearch != null && valueSearch !== search) {
            model.query = valueSearch;
            setSearch(model.query);
        }
        replaceHistory(page, pageSize, model.query);
    }, [valueSearch]);

    const changePage = (number) => {
        setPage(number);
        replaceHistory(number, pageSize, search);
    };

    const onChangeShowNumber = (e) => {
        setPageSize(e.target.value);
        replaceHistory(page, e.target.value, search);
    };

    const onChangePendingUser = (event) => {
        const checked = event.target.checked;
        setIsPendingUser(checked);
        setPage(1);
        setSelectedIds(new Set());
        replaceHistory(1, pageSize, search, checked);
    };

    const selectedRoleItems = normalizeSelectedRoles(selectedRoles);
    const selectedRoleNames = new Set(selectedRoleItems.map(getRoleName));
    const canSelectAccount = (account) => getAccountRoles(account).some((item) => {
        const roleName = getRoleName(item);
        return roleName.toUpperCase().startsWith(PENDING_ROLE_PREFIX)
            && selectedRoleNames.has(roleName);
    });
    const selectableIds = listAccount.filter(canSelectAccount).map((item) => item.id ?? item.Id);
    const isAllChecked = selectableIds.length > 0
        && selectableIds.every((id) => selectedIds.has(id));

    const toggleRow = (id) => {
        setSelectedIds((previous) => {
            const next = new Set(previous);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        setSelectedIds((previous) => {
            const next = new Set(previous);
            selectableIds.forEach((id) => isAllChecked ? next.delete(id) : next.add(id));
            return next;
        });
    };

    const onApplyRolesForSelected = async () => {
        if (selectedIds.size === 0 || selectedRoleItems.length === 0) return;
        setIsApproving(true);
        try {
            const response = await dispatch(
                approveInternalSoftwareAccessAction(Array.from(selectedIds))
            ).unwrap();
            toast.success(response?.message ?? 'Duyệt quyền truy cập phần mềm thành công');
            setSelectedIds(new Set());
            await replaceHistory(page, pageSize, search);
        } catch (error) {
            toast.error(error?.title ?? error?.message ?? 'Duyệt quyền truy cập phần mềm thất bại');
        } finally {
            setIsApproving(false);
        }
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title"> Danh sách tài khoản</h3>
                        </div>
                        <div className="card-body">
                            <div className="d-flex justify-content-between">
                                <form className="form-inline">
                                    <label
                                        htmlFor="exampleEmail2"
                                        className="mr-2"
                                    >
                                        Hiển thị :
                                    </label>
                                    <select
                                        className="form-control"
                                        value={pageSize}
                                        onChange={(e) => onChangeShowNumber(e)}
                                    >
                                        {objectShowNumber.map((item, key) => (
                                            <option
                                                key={Number(key)}
                                                value={item.value}
                                            >
                                                {item.text}
                                            </option>
                                        ))}
                                    </select>

                                    <div className="custom-control custom-checkbox ml-3">
                                        <input
                                            type="checkbox"
                                            className="custom-control-input"
                                            id="filterPendingUser"
                                            checked={isPendingUser}
                                            onChange={onChangePendingUser}
                                        />
                                        <label
                                            className="custom-control-label"
                                            htmlFor="filterPendingUser"
                                        >
                                            Chờ duyệt
                                        </label>
                                    </div>

                                    <div className="ml-3">
                                        <SelectTree
                                            data={lstRoleFlat}
                                            showSearchBox
                                            Placeholder="Chọn quyền được phép truy cập"
                                            positionExpand="right"
                                            multiSelect
                                            onChange={(valueArray) => {
                                                setSelectedRoles(normalizeSelectedRoles(valueArray));
                                                setSelectedIds(new Set());
                                            }}
                                        />
                                    </div>

                                    <button
                                        style={{marginLeft: '20px'}}
                                        type="button"
                                        className="btn btn-success ml-2"
                                        disabled={
                                            selectedIds.size === 0 ||
                                            selectedRoleItems.length === 0 ||
                                            isApproving
                                        }
                                        onClick={onApplyRolesForSelected}
                                        title="Duyệt quyền cho các tài khoản đã chọn"
                                    >
                                        {isApproving ? 'Đang duyệt...' : 'Duyệt quyền cho mục đã chọn'}
                                    </button>
                                </form>
                            </div>
                            <div
                                id="example2_wrapper"
                                className="dataTables_wrapper dt-bootstrap4"
                                style={{marginTop: '20px'}}
                            >
                                <div className="row">
                                    <div className="col-sm-12">
                                        <Table striped bordered hover>
                                            <thead>
                                                <tr role="row">
                                                    <th className="text-center" style={{width: 45}}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isAllChecked}
                                                            disabled={selectableIds.length === 0}
                                                            onChange={toggleAll}
                                                            aria-label="Chọn tất cả tài khoản đủ điều kiện"
                                                        />
                                                    </th>
                                                    <th className="sorting">
                                                        STT
                                                    </th>
                                                    <th className="sorting">
                                                        Tài khoản
                                                    </th>
                                                    <th className="sorting">
                                                        Họ tên
                                                    </th>
                                                    <th className="sorting">
                                                        Đơn vị
                                                    </th>
                                                    <th className="sorting">
                                                        Quyền
                                                    </th>
                                                    {/* <th className="sorting">
                                                        Bút danh
                                                    </th>
                                                    <th className="sorting">
                                                        Email
                                                    </th> */}
                                                    <th className="sorting">
                                                        Trạng thái
                                                    </th>
                                                </tr>
                                            </thead>
                                            {listAccount != null &&
                                            listAccount !== undefined &&
                                            listAccount.length > 0 ? (
                                                <tbody>
                                                    {listAccount.map(
                                                        (item, index) => {
                                                            const itemId = item.id ?? item.Id;
                                                            const canSelect = canSelectAccount(item);
                                                            const accountInfo = accountInfoByAccountId.get(itemId?.toLowerCase());
                                                            const units = accountInfo?.donVis ?? accountInfo?.DonVis ?? [];
                                                            const unitItems = Array.isArray(units) ? units : units?.$values ?? [];
                                                            return (
                                                                <tr
                                                                    key={itemId}
                                                                    className="odd"
                                                                >
                                                                    <td className="text-center">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedIds.has(itemId)}
                                                                            disabled={!canSelect}
                                                                            onChange={() => toggleRow(itemId)}
                                                                            aria-label={`Chọn tài khoản ${item.username}`}
                                                                        />
                                                                    </td>
                                                                    <td className="dtr-control sorting_1">
                                                                        {index +
                                                                            1 +
                                                                            (page -
                                                                                1) *
                                                                                pageSize}
                                                                    </td>
                                                                    <td>
                                                                        {
                                                                            item.username
                                                                        }
                                                                    </td>
                                                                    <td>
                                                                        {
                                                                            item.accountAuthInfo.lastName
                                                                        }{' '}
                                                                        {
                                                                            item.accountAuthInfo.middleName
                                                                        }{' '}
                                                                        {
                                                                            item.accountAuthInfo.firstName
                                                                        }
                                                                    </td>
                                                                    <td>
                                                                        {accountInfoQuery.isLoading ? 'Đang tải...' : unitItems
                                                                            .map((unit) => unit.tenDonVi ?? unit.TenDonVi ?? '')
                                                                            .filter(Boolean)
                                                                            .join(', ') || '—'}
                                                                    </td>
                                                                    <td>
                                                                        {getAccountRoles(item)
                                                                            .map(getRoleName)
                                                                            .join(', ')}
                                                                    </td>
                                                                    {/* <td>
                                                                    {
                                                                        item.fullname
                                                                    }
                                                                </td>
                                                                <td>
                                                                    {
                                                                        item.pseudonym
                                                                    }
                                                                </td>
                                                                <td>
                                                                    {item.email}
                                                                </td> */}
                                                                    <td>
                                                                        <div
                                                                            className={
                                                                                item.isActive
                                                                                    ? 'icheck-primary'
                                                                                    : 'icheck-danger'
                                                                            }
                                                                        >
                                                                            <input
                                                                                type="radio"
                                                                                id={
                                                                                    item.id
                                                                                }
                                                                                name={
                                                                                    item.id
                                                                                }
                                                                                defaultChecked
                                                                            />
                                                                            <label
                                                                                htmlFor={
                                                                                    item.id
                                                                                }
                                                                            >
                                                                                {item.isActive
                                                                                    ? 'Đã kích hoạt'
                                                                                    : 'Chưa kích hoạt'}
                                                                            </label>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }
                                                    )}
                                                </tbody>
                                            ) : (
                                                <tbody>
                                                    <tr className="txt-center">
                                                        <td colSpan="5">
                                                            Không có dữ liệu
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            )}
                                            <tfoot>
                                                <tr role="row">
                                                    <th></th>
                                                    <th className="sorting">
                                                        STT
                                                    </th>
                                                    <th className="sorting">
                                                        Tài khoản
                                                    </th>
                                                    <th className="sorting">
                                                        Họ tên
                                                    </th>
                                                    <th className="sorting">
                                                        Đơn vị
                                                    </th>
                                                    <th className="sorting">
                                                        Quyền
                                                    </th>
                                                    {/* <th className="sorting">
                                                        Họ tên
                                                    </th>
                                                    <th className="sorting">
                                                        Email
                                                    </th> */}
                                                    <th className="sorting">
                                                        Trạng thái
                                                    </th>
                                                    {/* <th className="sorting">
                                                        Chức năng
                                                    </th> */}
                                                </tr>
                                            </tfoot>
                                        </Table>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-sm-12">
                                        <div
                                            className="dataTables_paginate paging_simple_numbers"
                                            id="example2_paginate"
                                        >
                                            <Pagination
                                                itemClass="page-item"
                                                linkClass="page-link"
                                                innerClass="pagination pagination-sm no-margin pull-right"
                                                activePage={page}
                                                itemsCountPerPage={Number(
                                                    pageSize
                                                )}
                                                totalItemsCount={totalAccount}
                                                pageRangeDisplayed={5}
                                                onChange={(number) =>
                                                    changePage(number)
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DuyetAccount;
